import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Page, PageHeader, Empty } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CalendarRange, Trash2, Plus, Gauge, Users } from "lucide-react";
import { fmtNOK } from "@/lib/labels";
import { toast } from "sonner";

const curMonth = () => new Date().toISOString().slice(0, 7);

function utilColor(pct) {
  if (pct > 100) return "bg-red-500";
  if (pct >= 85) return "bg-emerald-500";
  if (pct >= 50) return "bg-amber-500";
  return "bg-zinc-500";
}

function Bar({ pct }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
        <div className={`h-full ${utilColor(pct)}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className="text-xs text-muted-foreground w-12 text-right">{pct}%</span>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <p className="label-caps">{label}</p>
      <p className={`font-display text-2xl mt-1 ${accent || ""}`}>{value}</p>
    </div>
  );
}

export default function Ressursplan() {
  const [month, setMonth] = useState(curMonth());
  const [cap, setCap] = useState(null);
  const [allocs, setAllocs] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ user_id: "", customer_id: "", planned_hours: "", note: "" });

  const load = useCallback(() => {
    api.get(`/capacity?month=${month}`).then((r) => setCap(r.data)).catch(() => {});
    api.get(`/allocations?month=${month}`).then((r) => setAllocs(r.data)).catch(() => {});
  }, [month]);

  useEffect(() => {
    api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveAlloc = async () => {
    if (!f.user_id || !f.planned_hours) { toast.error("Velg ansatt og angi planlagte timer"); return; }
    try {
      const u = users.find((x) => x.id === f.user_id);
      const c = customers.find((x) => x.id === f.customer_id);
      await api.post("/allocations", {
        user_id: f.user_id, user_name: u?.name, customer_id: f.customer_id || null,
        customer_name: c?.legal_name || null, month, planned_hours: Number(f.planned_hours), note: f.note,
      });
      toast.success("Ressursplan lagret");
      setOpen(false); setF({ user_id: "", customer_id: "", planned_hours: "", note: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const delAlloc = async (id) => { await api.delete(`/allocations/${id}`); load(); };

  const t = cap?.totals;

  return (
    <Page>
      <PageHeader title="Ressursplanlegging" subtitle="Kapasitet, planlagte og førte timer per ansatt" testid="ressursplan-header">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 bg-secondary border-border" data-testid="ressursplan-month" />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-white text-black hover:bg-white/90" data-testid="new-alloc-btn"><Plus className="w-4 h-4 mr-1" /> Planlegg</Button></DialogTrigger>
          <DialogContent className="bg-popover border-border">
            <DialogHeader><DialogTitle>Planlegg ressurs · {month}</DialogTitle>
              <DialogDescription>Fordel planlagte timer på en ansatt for valgt måned.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="label-caps">Ansatt</Label>
                <Select value={f.user_id} onValueChange={(v) => setF({ ...f, user_id: v })}>
                  <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="alloc-user-select"><SelectValue placeholder="Velg ansatt" /></SelectTrigger>
                  <SelectContent className="bg-popover border-border max-h-72">{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-caps">Kunde (valgfritt)</Label>
                <Select value={f.customer_id} onValueChange={(v) => setF({ ...f, customer_id: v })}>
                  <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="alloc-customer-select"><SelectValue placeholder="Velg kunde" /></SelectTrigger>
                  <SelectContent className="bg-popover border-border max-h-72">{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="label-caps">Planlagte timer</Label><Input type="number" value={f.planned_hours} onChange={(e) => setF({ ...f, planned_hours: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="alloc-hours" /></div>
              <div><Label className="label-caps">Notat</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="mt-1 bg-secondary border-border" /></div>
            </div>
            <DialogFooter><Button onClick={saveAlloc} className="bg-white text-black hover:bg-white/90" data-testid="alloc-save-btn">Lagre</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {t && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          <Stat label={`Kapasitet (${cap.business_days} virkedager)`} value={`${t.capacity_hours} t`} />
          <Stat label="Estimert fra kunder" value={`${t.estimated_hours} t`} accent="text-violet-400" />
          <Stat label="Planlagt totalt" value={`${t.planned_hours} t`} accent="text-blue-400" />
          <Stat label="Ført" value={`${t.actual_hours} t`} accent="text-emerald-400" />
          <Stat label="Kapasitetsutnyttelse" value={`${t.actual_utilization}%`} />
        </div>
      )}

      <Tabs defaultValue="kapasitet">
        <TabsList className="bg-secondary">
          <TabsTrigger value="kapasitet" data-testid="tab-kapasitet">Kapasitet</TabsTrigger>
          <TabsTrigger value="kunder" data-testid="tab-kunder">Per kunde</TabsTrigger>
          <TabsTrigger value="planlagt" data-testid="tab-planlagt">Manuelle allokeringer</TabsTrigger>
        </TabsList>

        <TabsContent value="kapasitet">
          <p className="text-xs text-muted-foreground mt-4 mb-2">«Planlagt» fylles automatisk fra kundenes produkter (MRR ÷ timepris) tilordnet oppdragsansvarlig, pluss eventuelle manuelle allokeringer.</p>
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left">{["Ansatt", "Kapasitet", "Estimert (kunder)", "Planlagt", "Ført", "Planlagt utn.", "Faktisk utn."].map((h) => <th key={h} className="px-4 py-3 label-caps">{h}</th>)}</tr></thead>
              <tbody>
                {(cap?.users || []).map((u) => (
                  <tr key={u.user_id} className="border-b border-border/50" data-testid={`cap-row-${u.user_id}`}>
                    <td className="px-4 py-3"><p className="font-medium">{u.user_name}</p><p className="text-xs text-muted-foreground">{u.department || "—"}</p></td>
                    <td className="px-4 py-3 text-muted-foreground">{u.capacity_hours} t</td>
                    <td className="px-4 py-3 text-violet-400">{u.estimated_hours} t{u.manual_hours > 0 && <span className="text-xs text-muted-foreground"> +{u.manual_hours} man.</span>}</td>
                    <td className="px-4 py-3 text-blue-400">{u.planned_hours} t</td>
                    <td className="px-4 py-3 text-emerald-400">{u.actual_hours} t</td>
                    <td className="px-4 py-3 w-40"><Bar pct={u.planned_utilization} /></td>
                    <td className="px-4 py-3 w-40"><Bar pct={u.actual_utilization} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(!cap?.users || cap.users.length === 0) && <div className="p-6"><Empty icon={Gauge} title="Ingen ansatte" /></div>}
          </div>
        </TabsContent>

        <TabsContent value="kunder">
          <p className="text-xs text-muted-foreground mt-4 mb-2">Estimert timebehov per aktiv kunde = månedlig produktpris (inkl. rabatt) ÷ timepris. Timene følger automatisk oppdragsansvarlig.</p>
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            {(cap?.customers || []).length === 0 ? <div className="p-6"><Empty icon={Users} title="Ingen kunder med estimert timebehov" hint="Legg til produkter og timepris på kundene." /></div> : (
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left">{["Kunde", "Oppdragsansvarlig", "MRR", "Timepris", "Estimert timer/mnd"].map((h) => <th key={h} className="px-4 py-3 label-caps">{h}</th>)}</tr></thead>
                <tbody>
                  {cap.customers.map((c) => (
                    <tr key={c.customer_id} className="border-b border-border/50" data-testid={`cust-cap-${c.customer_id}`}>
                      <td className="px-4 py-3 font-medium">{c.customer_name}</td>
                      <td className="px-4 py-3 text-muted-foreground">{c.engagement_manager_name || "—"}</td>
                      <td className="px-4 py-3">{fmtNOK(c.mrr)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{fmtNOK(c.hourly_rate)}</td>
                      <td className="px-4 py-3 text-violet-400 font-medium">{c.estimated_hours} t</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="planlagt">
          <div className="bg-card border border-border rounded-xl p-6 mt-4">
            {allocs.length === 0 ? <Empty icon={CalendarRange} title="Ingen ressurser planlagt" hint="Bruk «Planlegg» for å fordele timer på ansatte." /> : (
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left">{["Ansatt", "Kunde", "Planlagte timer", "Notat", ""].map((h) => <th key={h} className="px-3 py-2 label-caps">{h}</th>)}</tr></thead>
                <tbody>
                  {allocs.map((a) => (
                    <tr key={a.id} className="border-b border-border/50" data-testid={`alloc-row-${a.id}`}>
                      <td className="px-3 py-2 font-medium">{a.user_name}</td>
                      <td className="px-3 py-2 text-muted-foreground">{a.customer_name || "Generelt"}</td>
                      <td className="px-3 py-2 text-blue-400">{a.planned_hours} t</td>
                      <td className="px-3 py-2 text-muted-foreground">{a.note || "—"}</td>
                      <td className="px-3 py-2"><button onClick={() => delAlloc(a.id)} className="text-muted-foreground hover:text-destructive" data-testid={`alloc-del-${a.id}`}><Trash2 className="w-4 h-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </Page>
  );
}
